import { useState, Suspense, useCallback, useEffect, useRef } from 'react';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import Schedule from '../features/Schedule';
import DatePicker from '../features/DatePicker';
import GameDetails from './GameDetails';
import LiveScoreboard from '../components/LiveScoreboard';
import UmpSupplyInput from '../features/UmpSupplyInput';
import BallSupplyInput from '../features/BallSupplyInput';
import MeterTicks from '../features/BagMeter';
import PlayByPlay from '../features/PlayByPlay';
import StackedBarChart from '../features/StackedBarChart';
import { getContrastColors } from '../theme/teamColors';
import { useGameData } from '../hooks/useGameData';
import { useSelector } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Skeleton,
  Heading,
  Center,
  SimpleGrid,
  Icon,
  useColorMode,
  Switch,
  FormControl,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdFiberManualRecord, MdBrightness7, MdBrightness4, MdExitToApp, MdNotificationsActive } from 'react-icons/md';
import { IoIosTimer } from 'react-icons/io';
import { auth } from '../firebase';
import LegalModal from '../components/LegalModal';
import { requestNotificationPermission, sendNotification } from '../utils/notificationHelper';

const SettingsContent = ({ alertsEnabled, setAlertsEnabled }) => {
  const { colorMode, toggleColorMode } = useColorMode();
  const cardBg = "card.bg";
  const borderColor = "card.border";

  return (
    <VStack spacing={8} py={10} px={6} align="stretch">
      <Heading size="xs" color="hero.text" textTransform="uppercase" letterSpacing="widest" textAlign="center" fontWeight="900">
        APPLICATION SETTINGS
      </Heading>

      <Box bg={cardBg} p={6} border="1px solid" borderColor={borderColor} boxShadow="card.shadow">
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <HStack>
              <Icon 
                as={colorMode === 'light' ? MdBrightness7 : MdBrightness4} 
                color={colorMode === 'light' ? 'orange.400' : 'brand.blue'} 
                boxSize={5}
                filter={colorMode === 'light' ? "drop-shadow(0 0 8px orange)" : "drop-shadow(0 0 8px #00508D)"}
              />
              <Text fontWeight="900" color="hero.text" fontSize="sm">App Theme</Text>
            </HStack>
            <FormControl display="flex" alignItems="center" width="auto">
              <Switch 
                id="theme-mode" 
                colorScheme="red" 
                size="lg"
                isChecked={colorMode === 'dark'}
                onChange={toggleColorMode} 
              />
            </FormControl>
          </HStack>
          
          <HStack justify="space-between" pt={4} borderTop="1px dashed" borderColor={borderColor}>
            <HStack>
              <Icon as={MdNotificationsActive} color="brand.red" />
              <Text fontWeight="900" color="hero.text" fontSize="sm">Bag Alerts</Text>
            </HStack>
            <FormControl display="flex" alignItems="center" width="auto">
              <Switch 
                id="bag-alerts" 
                colorScheme="red" 
                size="lg"
                isChecked={alertsEnabled}
                onChange={(e) => setAlertsEnabled(e.target.checked)} 
              />
            </FormControl>
          </HStack>
          <Text fontSize="xs" color="hero.subtext" fontWeight="bold">
            Toggle automated warnings for supply levels. This includes in-app and background notifications.
          </Text>
        </VStack>
      </Box>

      <Box bg={cardBg} p={6} border="1px solid" borderColor={borderColor} boxShadow="card.shadow">
         <VStack align="stretch" spacing={4}>
            <Text fontWeight="900" color="hero.text" fontSize="sm">Account</Text>
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontSize="8px" color="hero.subtext" fontWeight="900" letterSpacing="widest">SIGNED IN AS</Text>
                <Text fontSize="sm" fontWeight="900" color="hero.text">{auth.currentUser?.email}</Text>
              </VStack>
              <Button 
                leftIcon={<MdExitToApp />} 
                size="sm" 
                variant="outline" 
                borderColor="brand.red"
                color="brand.red"
                fontWeight="900"
                _hover={{ bg: "brand.red", color: "white" }}
                onClick={() => auth.signOut()}
              >
                Logout
              </Button>
            </HStack>
         </VStack>
      </Box>

      <Box bg={cardBg} p={6} border="1px solid" borderColor={borderColor} boxShadow="card.shadow">
         <VStack align="stretch" spacing={2}>
            <Text fontWeight="900" color="hero.text" fontSize="sm">Version Info</Text>
            <Text fontSize="xs" color="hero.text" fontWeight="bold">Ballometer v3.7.0</Text>
            <Text 
              as="a" 
              href="mailto:douglas.beatty@mlb.com"
              fontSize="10px" 
              color="hero.subtext" 
              fontWeight="900" 
              letterSpacing="wide"
              _hover={{ color: "brand.red", textDecoration: "underline" }}
            >
              CREATED BY DOUG BEATTY
            </Text>
         </VStack>
      </Box>
    </VStack>
  );
};

const DashboardContent = ({ gamePk, totalBalls, alertsEnabled }) => {
  const { data } = useGameData(gamePk);
  const { colorMode } = useColorMode();
  const toast = useToast();
  const umpSupply = useSelector(state => state.umpSupply) || 0;
  
  // Alert Refs to prevent duplicate toasts
  const firstBagAlertRef = useRef(false);
  const firstBagEmptyAlertRef = useRef(false);
  const reserveAlertRef = useRef(false);

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  const isFinal = data?.data?.gameData?.status?.abstractGameState === 'Final';
  const isLive = data?.data?.gameData?.status?.abstractGameState === 'Live';

  useEffect(() => {
    // Suppress alerts if disabled, game is Final, or game hasn't started yet
    if (!alertsEnabled || isFinal || !isLive) {
      firstBagAlertRef.current = true;
      firstBagEmptyAlertRef.current = true;
      reserveAlertRef.current = true;
      return;
    }

    // First Bag Empty (Heads up)
    if (totalBalls?.firstBagMeter === 0 && !firstBagEmptyAlertRef.current) {
      const title = 'FIRST BAG EMPTY';
      const body = 'Starting reserve bag.';
      
      toast({
        id: 'first-bag-empty',
        title,
        description: body,
        status: 'info',
        duration: 5000,
        isClosable: true,
        containerStyle: {
          borderRadius: 'none',
        }
      });
      
      sendNotification(`Ballometer: ${title}`, body);
      firstBagEmptyAlertRef.current = true;
    } else if (totalBalls?.firstBagMeter > 0) {
      firstBagEmptyAlertRef.current = false;
    }

    // First Bag Low
    if (totalBalls?.firstBagMeter <= 24 && totalBalls?.firstBagMeter > 0 && !firstBagAlertRef.current) {
      const title = 'FIRST BAG LOW';
      const body = 'Supply is at 24, please deliver reserve bag.';
      
      toast({
        id: 'first-bag-low',
        title,
        description: body,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        containerStyle: {
          backgroundColor: '#BA0122', // brand.red — Chakra tokens don't resolve in containerStyle
          borderRadius: 'none',
        }
      });

      sendNotification(`Ballometer: ${title}`, body);
      firstBagAlertRef.current = true;
    } else if (totalBalls?.firstBagMeter > 24 || totalBalls?.firstBagMeter === 0) {
      firstBagAlertRef.current = false;
    }

    // Reserve Supply Alert
    if (totalBalls?.reserveBagMeter <= 24 && !reserveAlertRef.current && totalBalls?.reserveBagMeter > 0) {
      const title = 'RESERVE BAG LOW';
      const body = 'Supply is at 24, please add more baseballs.';

      toast({
        id: 'reserve-supply-low',
        title,
        description: body,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        containerStyle: {
          backgroundColor: '#BA0122', // brand.red — Chakra tokens don't resolve in containerStyle
          borderRadius: 'none',
        }
      });

      sendNotification(`Ballometer: ${title}`, body);
      reserveAlertRef.current = true;
    } else if (totalBalls?.reserveBagMeter > 24 || totalBalls?.reserveBagMeter === 0) {
      reserveAlertRef.current = false;
    }
  }, [totalBalls?.firstBagMeter, totalBalls?.reserveBagMeter, toast, isFinal, isLive, alertsEnabled]);

  const gameData = data?.data?.gameData;
  const liveData = data?.data?.liveData;

  // Most recent completed play description (the in-progress at-bat has none yet)
  const allPlays = liveData?.plays?.allPlays || [];
  const lastPlay = [...allPlays].reverse().find(p => p.result?.description)?.result.description?.trim();

  // Real-time Metrics Calculation
  const ballsUsed = totalBalls?.used || 0;
  // Peel the ump-pouch total off the event/slop telemetry. The full pouch is
  // still counted in the displayed TOTAL/REMAINING, but for pace it accrues
  // gradually instead of frontloading the whole pouch into inning one.
  const telemetryUsed = Math.max(0, ballsUsed - umpSupply);
  const currentInning = liveData?.linescore?.currentInning || 1;
  const inningHalf = liveData?.linescore?.inningHalf || 'Top';

  // Progress calculation: each half-inning is 0.5
  const inningProgress = (currentInning - 1) + (inningHalf === 'Bottom' ? 1.0 : 0.5);

  // Event/slop balls accrue with play; the ump pouch is a full-game total, so
  // spread it at umpSupply/9 per inning rather than counting it all up front.
  const eventRatePerInning = inningProgress > 0 ? telemetryUsed / inningProgress : 0;
  const ratePerInning = eventRatePerInning + umpSupply / 9;

  // Project across 8.75 innings — the midpoint of 8.5 (home team wins, bottom of
  // the 9th never batted) and 9 (full game) — so pace isn't biased high by a
  // half-inning that may never be played. Once actual progress passes 8.75
  // (bottom of the 9th, extras), project to the innings truly played.
  const paceInnings = Math.max(8.75, inningProgress);

  const perInning = inningProgress > 0 ? Math.round(ratePerInning) : "0";
  // Once the game is Final there's nothing left to project — pace is the actual total used
  const pace = isFinal
    ? ballsUsed
    : (inningProgress > 0 ? Math.round(ratePerInning * paceInnings) : "0");

  return (
    <VStack spacing={2} align="stretch" py={2} px={4} h="full">
      {/* QUICK STATS CENTER */}
      <Center flexDirection="column" pt={2}>
        <Text fontSize="10px" fontWeight="900" color="hero.subtext" letterSpacing="widest" mb={0}>
          TOTAL BALLS USED
        </Text>
        <Text 
          fontSize="7xl" 
          fontWeight="900" 
          lineHeight="1" 
          color="hero.text"
          textShadow={colorMode === 'dark' ? "0 0 15px rgba(255,255,255,0.15)" : "none"}
          fontFamily="'HEX Franklin MLB', sans-serif"
        >
          {ballsUsed}
        </Text>
        <Box w="80px" h="4px" bg="brand.red" mt={1} mb={3} boxShadow={colorMode === 'dark' ? "0 0 10px rgba(186, 1, 34, 0.5)" : "none"} />
        
        <HStack spacing={12}>
          <VStack spacing={0}>
            <Text fontSize="8px" fontWeight="bold" color="hero.subtext">PER INNING</Text>
            <Text fontSize="2xl" fontWeight="900" color="hero.text">{perInning}</Text>
          </VStack>
          <VStack spacing={0}>
            <Text fontSize="8px" fontWeight="bold" color="hero.subtext">PACE</Text>
            <Text fontSize="2xl" fontWeight="900" color="hero.text">{pace}</Text>
          </VStack>
          <VStack spacing={0}>
            <Text fontSize="8px" fontWeight="bold" color="hero.subtext">REMAINING</Text>
            <Text fontSize="2xl" fontWeight="900" color="hero.text">{totalBalls?.remaining || 0}</Text>
          </VStack>
        </HStack>
      </Center>

      {/* SUPPLY MANAGEMENT SECTION */}
      <Box bg="card.bg" p={4} border="1px solid" borderColor="card.border" boxShadow="card.shadow" borderRadius="md">
        <VStack spacing={4} align="stretch">
          <HStack spacing={6} justify="center">
            <UmpSupplyInput boxInfo={data?.data?.liveData?.boxscore?.info} />
            <BallSupplyInput />
          </HStack>

          {/* BAG METER INTEGRATION */}
          <Box pt={3} borderTop="1px solid" borderColor="card.border">
            <VStack spacing={4} align="stretch">
              <VStack spacing={1} align="stretch">
                <HStack justify="center" spacing={2} align="baseline">
                  <Text fontSize="8px" fontWeight="bold" color="hero.subtext" letterSpacing="widest" lineHeight="1">FIRST BAG</Text>
                  <Text 
                    fontSize="sm" 
                    fontWeight="900" 
                    color={(totalBalls?.firstBagMeter ?? 96) <= 24 ? "brand.red" : "hero.text"}
                    sx={{ fontFeatureSettings: "'tnum'" }}
                    transition="color 0.3s ease"
                    lineHeight="1"
                  >
                    {totalBalls?.firstBagMeter ?? 96}
                  </Text>
                </HStack>
                <Center>
                  <MeterTicks 
                    value={totalBalls?.firstBagMeter ?? 96} 
                    ticks={totalBalls?.firstBagTicks ?? [0, 12, 24, 36, 48, 60, 72, 84, 96]} 
                    threshold={24}
                  />
                </Center>
              </VStack>
              
              <VStack spacing={1} align="stretch">
                <HStack justify="center" spacing={2} align="baseline">
                  <Text fontSize="8px" fontWeight="bold" color="hero.subtext" letterSpacing="widest" lineHeight="1">RESERVE BAG</Text>
                  <Text 
                    fontSize="sm" 
                    fontWeight="900" 
                    color={(totalBalls?.reserveBagMeter ?? 0) <= 24 && (totalBalls?.reserveBagMeter ?? 0) > 0 ? "brand.red" : "hero.text"}
                    sx={{ fontFeatureSettings: "'tnum'" }}
                    transition="color 0.3s ease"
                    lineHeight="1"
                  >
                    {totalBalls?.reserveBagMeter ?? 0}
                  </Text>
                </HStack>
                <Center>
                  <MeterTicks 
                    value={totalBalls?.reserveBagMeter ?? 0} 
                    ticks={totalBalls?.reserveBagTicks ?? [0, 12, 24, 36, 48, 60, 72, 84]} 
                    threshold={24}
                  />
                </Center>
              </VStack>
            </VStack>
          </Box>
        </VStack>
      </Box>

      {/* LIVE SCOREBOARD CARD */}
      <Box flex="1" minH="0">
         <ErrorBoundary onReset={() => {}} fallbackRender={null}>
            <Suspense fallback={<Skeleton height="120px" />}>
               <LiveScoreboard gameData={gameData} liveData={liveData} lastPlay={lastPlay} />
            </Suspense>
         </ErrorBoundary>
      </Box>
    </VStack>
  );
};

const Home = () => {
  const { colorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const year = new Date().getFullYear();
  const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
  const day = new Date().getDate().toString().padStart(2, '0');
  const formattedDate = `${year}-${month}-${day}`;

  const [isoDate, setIsoDate] = useState(formattedDate);
  const [gamePk, setGamePk] = useState(null);
  const [totalBalls, setTotalBalls] = useState({ 
    remaining: 0, 
    used: 0, 
    bagMeter: 96, 
    ticks: [0, 12, 24, 36, 48, 60, 72, 84, 96],
    plays: [],
    currentInning: 1
  });
  const [activeTab, setActiveTab] = useState(0);
  const [alertsEnabled, setAlertsEnabled] = useState(() => {
    const saved = localStorage.getItem('bag-alerts-enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('bag-alerts-enabled', JSON.stringify(alertsEnabled));
  }, [alertsEnabled]);

  const { reset } = useQueryErrorResetBoundary();

  const handleDateChange = newIsoDate => {
    setIsoDate(newIsoDate);
    setGamePk(null);
  };

  const handleGamePkChange = newGamePk => {
    setGamePk(newGamePk);
    setActiveTab(0); // jump straight to the dashboard for the selected game
  };

  const updateTotalBallsData = useCallback(data => {
    setTotalBalls(data);
  }, []);

  return (
    <Box 
      bg="card.bg" 
      h="100vh" 
      overflow="hidden"
      position="relative"
      display="flex"
      flexDirection="column"
      backgroundImage={colorMode === 'light' ? "radial-gradient(circle, #E2E8F0 1px, transparent 1px)" : "none"}
      backgroundSize="20px 20px"
    >
      <Header />
      
      {/* LIVE DATA FEED Banner */}
      <Box 
        bg="black" 
        color="white" 
        py={2} 
        px={4} 
        boxShadow="md" 
        flexShrink={0}
        borderBottom="1px solid"
        borderColor="whiteAlpha.100"
      >
        <HStack justify="space-between" fontSize="9px" fontWeight="900" letterSpacing="widest" textTransform="uppercase">
          <HStack spacing={1}>
            <Icon as={MdFiberManualRecord} color="brand.red" />
            <Text>Live Data Feed</Text>
          </HStack>
          <Text>{gamePk ? `GAME ${gamePk} | ${(totalBalls?.gameStatus || 'ACTIVE').toUpperCase()}` : 'NO GAME SELECTED'}</Text>
        </HStack>
      </Box>

      {/* MAIN CONTENT AREA with Persistent Mounting */}
      <Box flex="1" overflowY={activeTab === 0 ? "hidden" : "auto"} pb="80px">
        {/* DASHBOARD TAB */}
        <Box display={activeTab === 0 ? "block" : "none"}>
          {gamePk ? (
            <ErrorBoundary onReset={reset} fallbackRender={null}>
              <Suspense fallback={<Center py={20}><Skeleton height="400px" width="300px" /></Center>}>
                <DashboardContent 
                  gamePk={gamePk} 
                  totalBalls={totalBalls} 
                  alertsEnabled={alertsEnabled}
                />
              </Suspense>
            </ErrorBoundary>
          ) : (
            <VStack py={20} px={6} spacing={6}>
              <Heading size="lg" color="hero.text" textAlign="center">COMMAND CENTER</Heading>
              <Text color="hero.subtext" textAlign="center">Select a game from the Schedule tab to begin tracking.</Text>
              <Button onClick={() => setActiveTab(1)}>Open Schedule</Button>
            </VStack>
          )}
        </Box>

        {/* SCHEDULE TAB */}
        <Box display={activeTab === 1 ? "block" : "none"}>
          <VStack spacing={0} w="full">
            <Box 
              w="full" 
              bg="card.bg" 
              p={6} 
              boxShadow="card.shadow" 
              borderBottom="2px solid" 
              borderColor="card.border"
              position="relative"

            >
              <Box position="absolute" bottom={0} left={0} right={0} h="2px" bg="brand.red" />
              <Heading size="xs" mb={4} color="hero.text" textTransform="uppercase" fontWeight="900" letterSpacing="widest">Select Date</Heading>
              <DatePicker isoDate={isoDate} onDateChange={handleDateChange} />
            </Box>
            
            <Box w="full" px={0} py={6}>
              <ErrorBoundary onReset={reset} fallbackRender={({ resetErrorBoundary }) => (
                <VStack alignContent="center" py={10}>
                  <Text>Error loading schedule.</Text>
                  <Button variant="solid" onClick={() => resetErrorBoundary()}>Retry</Button>
                </VStack>
              )}>
                <Suspense fallback={<SimpleGrid columns={2} spacing={4}><Skeleton height="100px" /><Skeleton height="100px" /></SimpleGrid>}>
                  <Schedule 
                    isoDate={isoDate} 
                    onGameClick={handleGamePkChange} 
                    currentGamePk={gamePk}
                  />
                </Suspense>
              </ErrorBoundary>
            </Box>
          </VStack>
        </Box>

        {/* STATS TAB */}
        <Box display={activeTab === 2 ? "block" : "none"}>
          <VStack spacing={5} py={6} px={6} align="stretch">
            <Heading size="xs" color="hero.text" textTransform="uppercase" letterSpacing="widest" textAlign="center" fontWeight="900">
              BALLS USED PER INNING
            </Heading>

            <Box bg="card.bg" p={4} border="1px solid" borderColor="card.border" boxShadow="card.shadow">
              {gamePk && totalBalls?.ballData?.some(d => d.amt > 0) ? (
                <VStack spacing={3} align="stretch">
                  <Box h="220px" w="full">
                    <StackedBarChart 
                      ballData={totalBalls.ballData} 
                      homeTeam={totalBalls.homeTeam} 
                      awayTeam={totalBalls.awayTeam} 
                    />
                  </Box>
                  <SimpleGrid columns={2} spacing={10} pt={4} borderTop="1px solid" borderColor="card.border">
                    <VStack align="center" spacing={0}>
                        <Text fontSize="8px" color="hero.subtext" fontWeight="900" letterSpacing="widest">HOME TEAM</Text>
                        <Text
                          fontSize="xl"
                          fontWeight="900"
                          color={getContrastColors(totalBalls.homeTeam, totalBalls.awayTeam, colorMode === 'dark').home}
                          textShadow={colorMode === 'dark' ? "0 0 10px rgba(255,255,255,0.1)" : "none"}
                        >
                          {totalBalls.homeTeam} {totalBalls.ballData.reduce((sum, entry) => sum + (entry[totalBalls.homeTeam] || 0), 0)}
                        </Text>
                    </VStack>
                    <VStack align="center" spacing={0}>
                        <Text fontSize="8px" color="hero.subtext" fontWeight="900" letterSpacing="widest">AWAY TEAM</Text>
                        <Text
                          fontSize="xl"
                          fontWeight="900"
                          color={getContrastColors(totalBalls.homeTeam, totalBalls.awayTeam, colorMode === 'dark').away}
                          textShadow={colorMode === 'dark' ? "0 0 10px rgba(255,255,255,0.1)" : "none"}
                        >
                          {totalBalls.awayTeam} {totalBalls.ballData.reduce((sum, entry) => sum + (entry[totalBalls.awayTeam] || 0), 0)}
                        </Text>
                    </VStack>
                  </SimpleGrid>
                </VStack>
              ) : (
                <Center py={24}>
                  <VStack spacing={6}>
                    <Icon as={gamePk ? IoIosTimer : IoIosTimer} w={12} h={12} color="gray.300" />
                    <VStack spacing={2}>
                      <Text color="hero.text" fontWeight="900" fontSize="md" textTransform="uppercase" letterSpacing="widest">
                        {gamePk ? "WAITING FOR FIRST PITCH" : "PLEASE SELECT A GAME"}
                      </Text>
                      <Text fontSize="xs" color="hero.subtext" textAlign="center" fontWeight="bold">
                        {gamePk ? "Data will populate when the game begins." : "Data will populate when a game is selected."}
                      </Text>
                    </VStack>
                  </VStack>
                </Center>
              )}
            </Box>

            {/* LIVE PLAY BY PLAY LOG */}
            {gamePk && totalBalls?.plays?.length > 0 && (
              <PlayByPlay
                plays={totalBalls.plays}
                currentInning={totalBalls.currentInning}
                homeColor={getContrastColors(totalBalls.homeTeam, totalBalls.awayTeam, colorMode === 'dark').home}
                awayColor={getContrastColors(totalBalls.homeTeam, totalBalls.awayTeam, colorMode === 'dark').away}
              />
            )}
          </VStack>
        </Box>

        {/* SETTINGS TAB */}
        <Box display={activeTab === 3 ? "block" : "none"}>
          <SettingsContent alertsEnabled={alertsEnabled} setAlertsEnabled={setAlertsEnabled} />
        </Box>
      </Box>

      {/* PERSISTENT TELEMETRY CALCULATION ENGINE */}
      {gamePk && (
        <Box display="none">
          <GameDetails gamePk={gamePk} updateTotalBalls={updateTotalBallsData} />
        </Box>
      )}

      <BottomNav activeTab={activeTab} onTabChange={(id) => setActiveTab(id)} />

      {activeTab === 3 && (
        <Text
          onClick={onOpen}
          cursor="pointer"
          position="fixed"
          bottom="100px"
          left="50%"
          transform="translateX(-50%)"
          fontSize="8px"
          fontWeight="900"
          letterSpacing="widest"
          color="hero.subtext"
          textAlign="center"
          zIndex={10}
          textTransform="uppercase"
          _hover={{ color: "brand.red" }}
        >
          Privacy Policy and Terms of Use
        </Text>
      )}

      <LegalModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

export default Home;
